(() => {
  const MOBILE_QUERY = '(max-width: 989px)';
  const PRODUCT_INFO_SELECTOR = '.product__info-container';
  const DETAILS_SELECTOR = `${PRODUCT_INFO_SELECTOR} .product__accordion > details`;
  const SUMMARY_SELECTOR = ':scope > summary';

  const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const OPEN_DURATION = 420;
  const CLOSE_DURATION = 320;

  const mobileQuery = window.matchMedia(MOBILE_QUERY);
  const trackedDetails = new WeakSet();
  const activeAnimations = new WeakMap();

  function isMobile() {
    return mobileQuery.matches;
  }

  function getSummary(details) {
    return details.querySelector(SUMMARY_SELECTOR) || details.querySelector('summary');
  }

  function getContent(details) {
    return details.querySelector('.accordion__content');
  }

  function setExpandedState(details, expanded) {
    const summary = getSummary(details);
    if (summary instanceof HTMLElement) {
      summary.setAttribute('aria-expanded', String(expanded));
    }
  }

  function getAccordionGroupRoot(details) {
    return details.closest(PRODUCT_INFO_SELECTOR) || document;
  }

  function cancelAnimation(details) {
    const running = activeAnimations.get(details);
    if (!running) return;

    running.heightAnimation?.cancel();
    running.contentAnimation?.cancel();
    activeAnimations.delete(details);

    details.classList.remove('is-animating');
    details.style.height = '';
    details.style.overflow = '';
  }

  async function animateDetail(details, expand) {
    const summary = getSummary(details);
    const content = getContent(details);
    if (!summary || !content) {
      details.open = expand;
      setExpandedState(details, expand);
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = expand;
      setExpandedState(details, expand);
      return;
    }

    cancelAnimation(details);

    if (expand && details.open) return;
    if (!expand && !details.open) return;

    const startHeight = details.offsetHeight;
    if (expand) details.open = true;
    const endHeight = expand ? summary.offsetHeight + content.offsetHeight : summary.offsetHeight;

    details.classList.add('is-animating');
    details.style.height = `${startHeight}px`;
    details.style.overflow = 'hidden';

    const heightAnimation = details.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      {
        duration: expand ? OPEN_DURATION : CLOSE_DURATION,
        easing: EASING,
        fill: 'forwards',
      }
    );

    const contentAnimation = content.animate(
      expand
        ? [
            { opacity: 0, transform: 'translateY(-1.2rem)', filter: 'blur(3px)' },
            { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
          ]
        : [
            { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
            { opacity: 0, transform: 'translateY(-0.8rem)', filter: 'blur(2px)' },
          ],
      {
        duration: expand ? OPEN_DURATION : CLOSE_DURATION,
        easing: EASING,
        fill: 'forwards',
      }
    );

    activeAnimations.set(details, { heightAnimation, contentAnimation });

    await Promise.allSettled([heightAnimation.finished, contentAnimation.finished]);

    if (!expand) details.open = false;

    setExpandedState(details, expand);
    details.classList.remove('is-animating');
    details.style.height = '';
    details.style.overflow = '';
    activeAnimations.delete(details);
  }

  function bindAccordion(details) {
    if (!(details instanceof HTMLDetailsElement) || trackedDetails.has(details)) return;

    const summary = getSummary(details);
    if (!(summary instanceof HTMLElement)) return;

    setExpandedState(details, details.open);

    summary.addEventListener('click', async (event) => {
      // Apply smooth transition to all devices as requested
      event.preventDefault();
      const shouldOpen = !details.open;
      const groupRoot = getAccordionGroupRoot(details);
      
      const animations = [];

      if (shouldOpen) {
        groupRoot.querySelectorAll(`${DETAILS_SELECTOR}[open]`).forEach((otherDetail) => {
          if (otherDetail !== details) {
            animations.push(animateDetail(otherDetail, false));
          }
        });
      }

      animations.push(animateDetail(details, shouldOpen));
      
      await Promise.allSettled(animations);
    });

    trackedDetails.add(details);
  }

  function initializeAccordions(root = document) {
    root.querySelectorAll(DETAILS_SELECTOR).forEach((details) => bindAccordion(details));
    
    // Only enforce single open on mobile initially to match existing behavior
    if (isMobile()) {
      const openAccordions = Array.from(root.querySelectorAll(`${DETAILS_SELECTOR}[open]`));
      if (openAccordions.length > 1) {
        openAccordions.slice(1).forEach(details => {
          details.open = false;
          setExpandedState(details, false);
        });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAccordions(), { once: true });
  } else {
    initializeAccordions();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAccordions(event.target);
  });

  const handleViewportChange = () => {
    // Only enforce if we switch TO mobile
    if (isMobile()) {
        const root = document.querySelector(PRODUCT_INFO_SELECTOR) || document;
        const openAccordions = Array.from(root.querySelectorAll(`${DETAILS_SELECTOR}[open]`));
        if (openAccordions.length > 1) {
          openAccordions.slice(1).forEach(details => {
            details.open = false;
            setExpandedState(details, false);
          });
        }
    }
  };

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', handleViewportChange);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(handleViewportChange);
  }
})();
